import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken, isAdminUser } from '../../Api/authToken';
import { FiCheckCircle, FiXCircle, FiInbox } from 'react-icons/fi';
import './AdminPages.css';

const PartnerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/partner-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (err) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    setUpdatingId(id);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/partner-requests/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Request ${status}`);
      fetchRequests();
    } catch (err) {
      toast.error('Failed to update request');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: { bg: '#fef3c7', color: '#92400e' },
      approved: { bg: '#d1fae5', color: '#065f46' },
      rejected:  { bg: '#fee2e2', color: '#991b1b' },
    };
    const s = map[status] || map.pending;
    return (
      <span style={{ padding: '0.25rem 0.625rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 600, background: s.bg, color: s.color }}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>Partner Requests</h1></div>
        <div className="admin-card"><div className="skeleton" style={{ height: '400px', borderRadius: '8px' }}></div></div>
      </div>
    );
  }

  const isAdmin = isAdminUser();

  return (
    <div className="admin-page">
      <div className="page-header flex-header">
        <div>
          <h1>Partner Requests</h1>
          <p>{requests.length} total request{requests.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="admin-card table-wrapper">
        {requests.length === 0 ? (
          <div className="empty-state">
            <FiInbox size={48} />
            <h3>No Requests Yet</h3>
            <p>Partner requests will appear here.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-hybrid-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Property</th>
                  <th>Partner</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td><span className="hybrid-id">#{r.id}</span></td>
                    <td><span className="info-pill">Property #{r.property_id}</span></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span style={{ fontWeight: 600, color: 'var(--admin-text-main)' }}>{r.partner_name || 'N/A'}</span>
                        {r.sub_register_name && <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>{r.sub_register_name}</span>}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.request_type}</span>
                    </td>
                    <td>{getStatusBadge(r.status)}</td>
                    <td>
                      <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                        {new Date(r.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    {isAdmin && (
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            className="btn-icon" 
                            style={{ color: '#10b981', borderColor: '#d1fae5', background: '#ecfdf5', opacity: r.status !== 'pending' ? 0.5 : 1 }}
                            title="Approve"
                            disabled={r.status !== 'pending' || updatingId === r.id}
                            onClick={() => handleStatusUpdate(r.id, 'approved')}
                          >
                            <FiCheckCircle size={15} />
                          </button>
                          <button 
                            className="btn-icon" 
                            style={{ color: '#ef4444', borderColor: '#fee2e2', background: '#fef2f2', opacity: r.status !== 'pending' ? 0.5 : 1 }}
                            title="Reject"
                            disabled={r.status !== 'pending' || updatingId === r.id}
                            onClick={() => handleStatusUpdate(r.id, 'rejected')}
                          >
                            <FiXCircle size={15} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerRequests;
